import React from 'react'
import { useTranslation } from 'react-i18next'
import { CanvasWindowCloseIcon, CanvasWindowMaximizeIcon, CanvasWindowRestoreIcon } from './icons'
import type { CanvasWindowModeToggleProps } from './CanvasWindowModeToggle.types'
import './CanvasWindowModeToggle.scss'
import { WithTooltip } from '@/src/renderer/components/ui/tooltip'

/**
 * Die Fenster-Umschaltgruppe oben rechts im geöffneten Projekt-Canvas
 * (Figma „Window Mode Toggle Group" 4872:100220): zwei Segmente in einer
 * gerahmten Pille, getrennt von einer Haarlinie.
 *
 * Links wechselt zwischen halber Breite und voller Fläche — das Icon zeigt
 * dabei stets die AKTION, nicht den Zustand: im geteilten Zustand das
 * Pfeilkreuz (maximieren), im maximierten den Seitenbalken (Liste zurück).
 * Rechts schließt den Canvas.
 *
 * @example
 * <CanvasWindowModeToggle
 *   state="split"
 *   onToggleMaximize={() => setPanelState('maximized')}
 *   onClose={() => setPanelState('closed')}
 * />
 */
export const CanvasWindowModeToggle: React.FC<CanvasWindowModeToggleProps> = ({
  state, onToggleMaximize, onClose, className,
}) => {
  const { t } = useTranslation()

  const maximizeLabel = state === 'maximized'
    ? t('projectCanvas.toolbar.restore')
    : t('projectCanvas.toolbar.maximize')

  return (
    <div className={`canvas-window-mode-toggle${className ? ` ${className}` : ''}`}>
      {/* `C` holt den Canvas maximiert herein (`useCanvasPanelShortcut`) — nur am Maximieren, nicht am Wiederherstellen. */}
      <WithTooltip content={maximizeLabel} shortcut={state === 'maximized' ? undefined : { kind: 'character', text: 'C' }}>
        <button
          type="button"
          className="canvas-window-mode-toggle__button"
          aria-label={maximizeLabel}
          onClick={onToggleMaximize}
        >
          {state === 'maximized'
            ? <CanvasWindowRestoreIcon size={20} />
            : <CanvasWindowMaximizeIcon size={20} />}
        </button>
      </WithTooltip>

      <span className="canvas-window-mode-toggle__separator" aria-hidden="true" />

      <WithTooltip content={t('projectCanvas.toolbar.close')}>
        <button
          type="button"
          className="canvas-window-mode-toggle__button"
          aria-label={t('projectCanvas.toolbar.close')}
          onClick={onClose}
        >
          <CanvasWindowCloseIcon size={20} />
        </button>
      </WithTooltip>
    </div>
  )
}
